import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import { accountCardStyles } from './styles';
import { Edit, Delete } from '@mui/icons-material';
import { NotesIcon } from 'components/Shared/Icons/tooltip_icons';
import { Account } from 'types/entities/types';
import { useState } from 'react';
import axiosService from 'utils/axios';
import AddOrEditAccount from './AddOrEditAccount';
import CardTitle from 'components/Shared/CardsView/CardTitle';
import ModalErrorPopup from '../../../components/Modal/ModalErrorPopup';
import { ErrorResponse } from '../../../types/general/types';

interface Props {
  option: Account;
  showActionButtons: boolean;
  onAccountDelete?: () => void;
  onAccountUpdate?: () => void;
}

const AccountCard = (props: Props) => {
  const { option: account, showActionButtons = true, onAccountDelete, onAccountUpdate } = props;

  const [editAccountModalOpen, setEditAccountModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<ErrorResponse | null>();

  const deleteAccount = () => {
    axiosService
      .delete(`/entities/account/${account.id}/`)
      .then(() => {
        if (onAccountDelete) {
          onAccountDelete();
        }
      })
      .catch(error => setDeleteError(error))
      .finally(() => setDeleteDialogOpen(false));
  };

  const onAccountEdit = () => {
    if (onAccountUpdate) {
      onAccountUpdate();
    }
    setEditAccountModalOpen(false);
  };

  return (
    <>
      <Card elevation={1} sx={accountCardStyles.optionCard}>
        <CardHeader
          action={
            showActionButtons ? (
              <>
                <Tooltip title="Edit">
                  <IconButton
                    sx={accountCardStyles.cardAction}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditAccountModalOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    sx={accountCardStyles.cardAction}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </>
            ) : null
          }
          title={<CardTitle title={account.name} tooltipText={account.id} />}
          subheader="Account"
          sx={accountCardStyles.cardHeader}
        />
        <CardContent>
          <Box sx={accountCardStyles.contactBox}>
            <NotesIcon />
            <Typography color="text" sx={accountCardStyles.contactValue}>
              {account.notes || '-'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle id="alert-dialog-title">Delete account</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete account <b>{account.name}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={deleteAccount} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <AddOrEditAccount
        account={account}
        onSave={onAccountEdit}
        modalTitle="Edit account"
        isModalOpen={editAccountModalOpen}
        setModalOpen={setEditAccountModalOpen}
      />
      {/* @ts-ignore:next-line*/}
      <ModalErrorPopup
        title={deleteError?.statusText}
        message={deleteError?.message}
        onClose={() => setDeleteError(null)}
      />
    </>
  );
};

export default AccountCard;
